import axios from 'axios';

/**
 * Vuex module for public Warehouse endpoints.
 *
 * Notes:
 * - Uses global API prefix window.navPublicTomcatApi defined in src/Services/GlobalVariables.js
 * - Do not hardcode full URLs here; follow global variables policy.
 */
export default {
  // keep non-namespaced to align with existing modules, but use unique action name to avoid conflicts
  state: {},
  getters: {},
  mutations: {},
  actions: {
    /**
     * Fetch pallet checkout information by pallet number and normalize it for the UI.
     *
     * Normalization rules:
     * - Map API `quantity` → `scannedQty` (what the UI expects)
     * - Derive `itemCode` and `uom` by splitting `plu`:
     *   Example: `MDZ-673835-CS-24` → `itemCode: MDZ-673835`, `uom: CS-24`
     *   Example: `MDZ-677154-CS-20` → `itemCode: MDZ-677154`, `uom: CS-20`
     * - Pass through `barcode` so the UI can show it
     * - Keep original `plu` for display/reference
     *
     * @param {Object} _context - Vuex action context (unused).
     * @param {string} palletNumber - The pallet number to look up.
     * @returns {Promise<Object>} Normalized pallet info object.
     * @throws {Error} When the pallet is not found (404) or on other errors.
     */
    async fetchPalletInfo(_context, palletNumber) {
      if (!palletNumber) {
        throw new Error('No pallet number provided');
      }

      // Helper to split PLU into itemCode and uom safely
      const splitPlu = (plu) => {
        if (!plu || typeof plu !== 'string') return { itemCode: '', uom: '' };
        const parts = plu.split('-').filter(Boolean);
        // Prefer a general rule: itemCode = all but last 2 segments; uom = last 2 segments
        if (parts.length >= 4) {
          return {
            itemCode: parts.slice(0, parts.length - 2).join('-'),
            uom: parts.slice(-2).join('-'),
          };
        }
        // Fallbacks for shorter/irregular PLUs
        if (parts.length >= 2) {
          return { itemCode: parts[0], uom: parts.slice(1).join('-') };
        }
        return { itemCode: plu, uom: '' };
      };

      try {
        const url = `${navPublicTomcatApi}checkout/pallet`;
        const { data } = await axios.get(url, { params: { palletNumber } });

        // Normalize for UI
        const normalized = {
          palletId: data.palletId,
          pallet: data.pallet,
          items: (data.items || []).map((x) => {
            const { itemCode, uom } = splitPlu(x.plu);
            return {
              // Keep originals
              plu: x.plu,
              description: x.description,
              // Derived/renamed
              itemCode,
              uom,
              scannedQty: x.quantity,
              // Pass-throughs for display
              barcode: x.barcode,
              // Optional references you might want later
              pickingItemId: x.pickingItemId,
              palletItemId: x.palletItemId,
            };
          }),
        };

        return normalized;
      } catch (err) {
        // Normalize error
        const res = err && err.response;
        if (res && res.status === 404) {
          const message = (res.data && (res.data.message || res.data.error)) || `Pallet not found: ${palletNumber}`;
          throw new Error(message);
        }
        // Let global error handler show details if available
        if (typeof errorHandler !== 'undefined' && errorHandler && errorHandler.tomcatError) {
          errorHandler.tomcatError(err);
        }
        throw new Error('Failed to fetch pallet information');
      }
    },
  },
};
